import * as THREE from 'three'
import TWEEN from '@tweenjs/tween.js'
import config from '@/config/main'
import { mapTo, distance3 } from '@/utils/math'

/**
 * The Stage handles the scene, lighting, camera, etc.
 */
export default class Stage {
  mousePosition = new THREE.Vector2()
  followMouse = true
  cameraPosition = null
  cameraIsMoving = false

  constructor (elem) {
    this.root = document.querySelector(elem)
    this.debug = process.env.NODE_ENV !== 'production'

    this.createScene()
    this.createCamera()
    this.createLight()
    this.createRenderer()

    this.bindEvents()

    if (this.debug) {
      this.enableDebugMode()
    } else {
      window.forceDebug = () => {
        this.debug = true
        this.enableDebugMode()
      }
    }
  }

  bindEvents () {
    window.addEventListener('resize', () => this.onResize(), false)
    window.addEventListener('orientationchange', () => this.onOrientationChange(), false)
    this.root.addEventListener('mousemove', e => this.onMouseMove(e), false)
    this.root.addEventListener('touchmove', e => this.onTouchMove(e), false)
    // this.root.addEventListener('mouseout', this.onMouseOut.bind(this), false)
  }

  createScene () {
    const { background, fog } = config.scene
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(background)
    this.scene.fog = new THREE.Fog(fog.color, fog.near, fog.far)
  }

  createCamera () {
    const { fov, near, far, position: { x, y, z } } = config.camera
    this.camera = new THREE.PerspectiveCamera(fov, window.innerWidth / window.innerHeight, near, far)
    this.camera.position.set(x, y, z)
    this.cameraPosition = new THREE.Vector3(x, y, z)
    this.cameraIsMoving = false
  }

  createLight () {
    const { color, intensity, position } = config.light
    this.light = new THREE.DirectionalLight(color, intensity)
    this.light.position.copy(position)
    this.scene.add(this.light)
  }

  createRenderer () {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    })

    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.root.appendChild(this.renderer.domElement)
  }

  addControls () {
    const OrbitControls = require('three/examples/jsm/controls/OrbitControls').OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableKeys = false
    this.controls.addEventListener('change', () => {
      console.log(this.camera.position.clone().round())
    })
  }

  addHelpers () {
    const axesHelper = new THREE.AxesHelper(1000)
    this.add(axesHelper)

    const lightHelper = new THREE.DirectionalLightHelper(this.light, 10)
    this.add(lightHelper)
  }

  onResize () {
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(window.innerWidth, window.innerHeight)
  }

  enableDebugMode () {
    this.addControls()
    this.addHelpers()
  }

  onOrientationChange () {
    setTimeout(() => this.onResize(), 400)
  }

  onMouseMove (e) {
    const d = distance3(this.cameraPosition, this.scene.position)
    this.mousePosition.x = mapTo(e.clientX, 0, window.innerWidth, this.cameraPosition.x - d * 0.1, this.cameraPosition.x + d * 0.1)
    this.mousePosition.y = mapTo(e.clientY, 0, window.innerHeight, this.cameraPosition.y - d * 0.1, this.cameraPosition.y + d * 0.1)
  }

  onTouchMove (e) {
    e.preventDefault()
    if (e.touches.length) {
      const { pageX, pageY } = e.touches[0]
      this.onMouseMove({ clientX: pageX, clientY: pageY })
    }
  }

  add (obj) {
    this.scene.add(obj)
  }

  remove (obj) {
    this.scene.remove(obj)
  }

  setFocus (x, y, z) {
    this.camera.lookAt(x, y, z)
  }

  changeFocus (x, y, z, duration = 2000) {
    return new Promise(resolve => {
      this.cameraIsMoving = true

      const rotation = this.camera.rotation.clone()
      this.camera.lookAt(x, y, z)
      const target = this.camera.rotation.clone()
      this.camera.rotation.copy(rotation)

      new TWEEN.Tween(this.camera.rotation)
        .to({ x: target.x, y: target.y, z: target.z }, duration)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onComplete(() => {
          this.cameraIsMoving = false
          resolve()
        })
        .start()
    })
  }

  setCameraPosition (x, y, z) {
    this.mousePosition.set(x, y)
    this.cameraPosition.set(x, y, z)
    this.camera.position.set(x, y, z)
    this.camera.lookAt(this.scene.position)
    this.controls && this.controls.update()
  }

  changeCameraPosition (x, y, z, duration = 2000) {
    return new Promise(resolve => {
      this.cameraIsMoving = true

      const update = () => this.camera.lookAt(this.scene.position)

      const done = () => {
        this.mousePosition.set(x, y)
        this.cameraPosition.set(x, y, z)
        this.cameraIsMoving = false
        this.controls && this.controls.update()
        resolve()
      }

      new TWEEN.Tween(this.camera.position)
        .to({ x, y, z }, duration)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onUpdate(update)
        .onComplete(done)
        .start()
    })
  }

  setFog (color, near, far) {
    this.scene.fog = new THREE.Fog(color, near, far)
  }

  setFogDistance (near, far) {
    this.scene.fog.near = near
    this.scene.fog.far = far
  }

  changeFogColor (color, duration = 2000) {
    return new Promise(resolve => {
      const { r, g, b } = new THREE.Color(color)
      new TWEEN.Tween(this.scene.fog.color)
        .to({ r, g, b }, duration)
        .onComplete(resolve)
        .start()
    })
  }

  changeFogDistance (near, far, duration = 2000) {
    return new Promise(resolve => {
      new TWEEN.Tween(this.scene.fog)
        .to({ near, far }, duration)
        .onComplete(resolve)
        .start()
    })
  }

  setBackground (color) {
    this.scene.background = new THREE.Color(color)
  }

  changeBackground (color, duration = 2000) {
    return new Promise(resolve => {
      const { r, g, b } = new THREE.Color(color)
      new TWEEN.Tween(this.scene.background)
        .to({ r, g, b }, duration)
        .easing(TWEEN.Easing.Linear.None)
        .onComplete(resolve)
        .start()
    })
  }

  fadeIn (duration = 1000) {
    const { near, far } = config.scene.fog
    return this.changeFogDistance(near, far, duration)
  }

  fadeOut (duration = 1000) {
    return this.changeFogDistance(1, 1, duration)
  }

  crossFade (duration = 1000) {
    const { near, far } = this.scene.fog
    const fade = (n, f) => this.changeFogDistance(n, f, duration / 2)
    return fade(1, 1).then(() => fade(near, far))
  }

  update () {
    TWEEN.update()

    if (this.followMouse && !this.cameraIsMoving && !this.debug) {
      this.camera.position.x += 0.03 * (this.mousePosition.x - this.camera.position.x)
      this.camera.position.y += 0.03 * (this.mousePosition.y - this.camera.position.y)
      // this.camera.lookAt(this.scene.position)
    }

    this.renderer.render(this.scene, this.camera)
  }
}
